/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import dayjs from "dayjs";
import { Cell } from "react-table";
import { Button, SVGIcon, Text, Tooltip } from "@nordcloud/gnui";
import { ACTION, PopoverControls } from "~/components";
import { KpiTarget } from "~/components/KPI/types";
import { dateFormat, KpiStatus } from "~/constants";
import { PathName, trimText } from "~/tools";

type Props = {
  onArchiveActionClick?: (kpi: KpiTarget) => void;
  onEditActionClick?: (kpi: KpiTarget) => void;
};

export function KpiTargetsListColumns({
  onArchiveActionClick,
  onEditActionClick,
}: Props = {}) {
  return [
    {
      accessor: "status",
      disableSortBy: true,
      width: 20,
      Cell: ({ row }: Cell<KpiTarget>) => {
        const status = getKpiStatus(
          row.original.startDate,
          row.original.endDate
        );

        if (!onArchiveActionClick) {
          return null;
        }

        return (
          <div css={{ position: "relative" }}>
            <Tooltip
              position="left"
              minWidth={status.minWidth}
              caption={status.tooltipText}
              status={status.color}
              data-testid="status-tooltip"
            >
              <SVGIcon
                data-testid="status-icon"
                name={status.icon}
                css={{ verticalAlign: "middle" }}
              />
            </Tooltip>
          </div>
        );
      },
    },
    {
      Header: "Target Name",
      accessor: "name",
      disableSortBy: true,
      width: 120,
      Cell: ({ row }: Cell<KpiTarget>) => (
        <Text tag="div">{trimText(row.original.name ?? "", 30)}</Text>
      ),
    },
    {
      Header: "Target Value",
      accessor: "targetValue",
      disableSortBy: true,
      width: 70,
      Cell: ({ row }: Cell<KpiTarget>) => (
        <Text tag="div">{row.original.goal}%</Text>
      ),
    },
    {
      Header: "Target Contact Person",
      accessor: "contactPerson",
      disableSortBy: true,
      width: 70,
      Cell: ({ row }: Cell<KpiTarget>) => (
        <Text tag="div">{row.original.contactPerson?.name}</Text>
      ),
    },
    {
      Header: "Start Date",
      accessor: "startDate",
      disableSortBy: true,
      width: 70,
      Cell: ({ row }: Cell<KpiTarget>) => (
        <Text tag="div">
          {dayjs(row.original.startDate).format(dateFormat.dayMonthYear)}
        </Text>
      ),
    },
    {
      Header: "Review Date",
      accessor: "endDate",
      disableSortBy: true,
      width: 70,
      Cell: ({ row }: Cell<KpiTarget>) => (
        <Text tag="div">
          {dayjs(row.original.endDate).format(dateFormat.dayMonthYear)}
        </Text>
      ),
    },
    {
      accessor: "actions",
      disableSortBy: true,
      width: 20,
      align: "right",
      Cell: ({ row }: Cell<KpiTarget>) => {
        if (!onArchiveActionClick || !onEditActionClick) {
          return null;
        }

        return (
          <PopoverControls
            trigger={<Button severity="low" icon="menu" size="md" />}
            items={[
              {
                label: "Edit Target",
                customIcon: "edit",
                action: ACTION.CUSTOM_TRIGGER,
                onClick: () => onEditActionClick(row.original),
              },
              {
                label: "Archive Target",
                customIcon: "archive",
                action: ACTION.CUSTOM_TRIGGER,
                onClick: () => onArchiveActionClick(row.original),
              },
            ]}
          />
        );
      },
    },
  ];
}

type Status = {
  status: KpiStatus;
  tooltipText: string;
  icon: PathName;
  color: React.ComponentProps<typeof Tooltip>["status"];
  minWidth: string;
};

function getKpiStatus(startDate: string, endDate: string): Status {
  const currentDate = dayjs();

  if (dayjs(startDate).isAfter(currentDate)) {
    return {
      status: KpiStatus.PLANNED,
      tooltipText: "Planned",
      icon: "statusOnHold",
      color: "warning",
      minWidth: "2.5rem",
    };
  }

  if (dayjs(endDate).isBefore(currentDate)) {
    return {
      status: KpiStatus.ACHIEVED,
      tooltipText: "Completed",
      icon: "statusSuccess",
      color: "success",
      minWidth: "2.5rem",
    };
  }

  return {
    status: KpiStatus.IN_PROGRESS,
    tooltipText: "In Progress",
    icon: "statusInProgress",
    color: "notification",
    minWidth: "4rem",
  };
}
